import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { usersApi } from '../api/users';
import { usageApi } from '../api/usage';
import { departmentsApi } from '../api/departments';
import { publicApi } from '../api/public';
import { Link } from 'react-router-dom';
import { Check, Star, Award } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import ProfileForm from '../components/profile/ProfileForm';
import Card from '../components/ui/Card';
import { User, CreditCard, ShoppingBag, Clock } from 'lucide-react';

const Profile = () => {
  const { user } = useAuth();
  
  // Завантаження департаментів з API замість статичного списку
  const { data: departments = [], isLoading: departmentsLoading } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      try {
        // Спочатку пробуємо авторизований ендпоінт
        const res = await departmentsApi.getAllDepartments();
        console.log('Profile - Departments API response:', res);
        
        // Обробляємо різні структури відповіді від backend
        if (res.data) {
          if (Array.isArray(res.data)) {
            return res.data;
          }
          if (res.data.data && Array.isArray(res.data.data)) {
            return res.data.data;
          }
          if (res.data.departments && Array.isArray(res.data.departments)) {
            return res.data.departments;
          }
        }
        
        console.warn('Profile - Unexpected departments API response structure:', res);
        return [];
      } catch (error) {
        console.warn('Profile - Failed to fetch departments from main API, trying public API:', error);
        
        try {
          // Fallback на публічний ендпоінт
          const publicRes = await publicApi.getDepartments();
          console.log('Profile - Public departments API response:', publicRes);
          
          if (publicRes.data && Array.isArray(publicRes.data)) {
            return publicRes.data;
          }
          
          console.warn('Profile - Unexpected public departments API response structure:', publicRes);
          return [];
        } catch (publicError) {
          console.error('Profile - Both departments APIs failed:', publicError);
          return [];
        }
      }
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 години
    retry: 1,
  });
  
  const { data: savingsData } = useQuery({
    queryKey: ['userSavings', user?.id],
    queryFn: () => usageApi.getTotalSavingsByUser(user?.id).then(res => res.data),
    enabled: !!user?.id,
  });
  
  const { data: usageHistory } = useQuery({
    queryKey: ['userUsageHistory', user?.id],
    queryFn: () => usageApi.getUserUsages(user?.id).then(res => res.data),
    enabled: !!user?.id,
  });

  // Знаходимо назву департаменту користувача
  const userDepartment = departments.find(dept => dept.id === user?.departmentId);
  const departmentName = userDepartment?.name || 'Не вказано';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Особистий кабінет</h1>
          <p className="mt-2 text-gray-600">Керуйте своїм профілем та переглядайте статистику</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Лівий стовпець - Профіль */}
          <div className="lg:col-span-2">
            <ProfileForm departments={departments} departmentsLoading={departmentsLoading} />
          </div>

          {/* Правий стовпець - Статистика */}
          <div className="space-y-6">
            {/* Картка користувача */}
            <Card className="p-6">
              <div className="flex items-center space-x-4 mb-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-medium text-gray-900 truncate">
                    {user?.fullName || 'Користувач'}
                  </h3>
                  <p className="text-sm text-gray-500 truncate">{user?.email}</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Підрозділ:</span>
                  <span className="text-gray-900 text-right">{departmentName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Посада:</span>
                  <span className="text-gray-900 text-right">{user?.position || 'Не вказано'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Член профспілки:</span>
                  <span className="text-gray-900">
                    {user?.isUnionMember ? (
                      <span className="flex items-center text-green-600">
                        <Check size={16} className="mr-1" />
                        Так
                      </span>
                    ) : (
                      <span className="text-gray-500">Ні</span>
                    )}
                  </span>
                </div>
              </div>
            </Card>

            {/* Статистика заощаджень */}
            <Card className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Заощадження</h3>
                  <p className="text-sm text-gray-500">Ваші загальні економії</p>
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {savingsData?.totalSavings ? `${savingsData.totalSavings.toLocaleString()} грн` : '0 грн'}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  За весь період користування
                </p>
              </div>
            </Card>

            {/* Статистика використання */}
            <Card className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <ShoppingBag className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Використання</h3>
                  <p className="text-sm text-gray-500">Статистика знижок</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Всього використано:</span>
                  <span className="font-medium text-gray-900">
                    {usageHistory?.length || 0}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Цього місяця:</span>
                  <span className="font-medium text-gray-900">
                    {usageHistory?.filter(usage => {
                      const usageDate = new Date(usage.usedAt);
                      const now = new Date();
                      return usageDate.getMonth() === now.getMonth() && 
                             usageDate.getFullYear() === now.getFullYear();
                    }).length || 0}
                  </span>
                </div>
              </div>
            </Card>

            {/* Останні активності */}
            <Card className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Останні активності</h3>
                  <p className="text-sm text-gray-500">Нещодавні використання знижок</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {usageHistory && usageHistory.length > 0 ? (
                  usageHistory.slice(0, 3).map((usage, index) => (
                    <div key={index} className="flex justify-between items-center text-sm">
                      <div>
                        <div className="font-medium text-gray-900 truncate">
                          {usage.discount?.title || 'Знижка'}
                        </div>
                        <div className="text-gray-500">
                          {usage.partner?.name || 'Партнер'}
                        </div>
                      </div>
                      <div className="text-right text-gray-500">
                        {new Date(usage.usedAt).toLocaleDateString('uk-UA')}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">
                    Ви ще не використовували жодної знижки
                  </p>
                )}
              </div>
              
              {usageHistory && usageHistory.length > 3 && (
                <div className="mt-4 pt-4 border-t">
                  <Link 
                    to="/usage-history" 
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Переглянути всю історію →
                  </Link>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;